/**
 * useMessengerPost — un envoi dans un fil de la messagerie (`postMessages`, `postImage`,
 * `postDocument`), une issue par envoi.
 *
 * L'idiome d'écriture des ports (`ui/hooks/useApiMutation`), écrit à la main pour DEUX gardes de
 * plus, qu'il dit :
 *  - la réponse est la ressource du message NON enveloppée (`MessengerApi` : `withoutWrapping`) :
 *    elle est dans `all_data`, pas dans `data` que lit `useApiMutation` ;
 *  - un envoi appartient à UN message de la boîte d'envoi (`localId`) et à UN fil
 *    (`routeParams.threadId`) : la tranche est partagée par toutes les conversations montées.
 * Un seul envoi en vol par service, comme l'ancien fil qui retirait ses boutons pendant l'envoi.
 */
import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { apiIdle, apiRequest } from "../../../../../../data/redux/actions/api"
import { useGesture } from "../../../hooks/gestureTracking"
import { type ApiService } from "../../../hooks/useApiFetch"
import {
    type ApiMessengerFailure,
    type ApiMessengerMessage,
} from "../../../../adapters/student/Messaging/studentConversationAdapter"

type PostSlice = {
    success?: boolean
    error?: ApiMessengerFailure
    routeParams?: { threadId?: string | number }
    all_data?: ApiMessengerMessage
}

/** Refusé, l'envoi garde ce que la tranche en dit : son motif se lit sous la bulle. */
export type MessengerPostOutcome =
    { ok: true; server: ApiMessengerMessage | null } | { ok: false; error: ApiMessengerFailure }

export function useMessengerPost(
    service: ApiService,
    onDone: (localId: string, outcome: MessengerPostOutcome) => void
) {
    const dispatch = useDispatch()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const req = useSelector((state: any) => state?.api?.[service.name] as PostSlice | undefined)
    const inFlightRef = useRef<{ localId: string; threadId: string } | null>(null)
    const [busy, setBusy] = useState(false)
    const gesture = useGesture("message.send")
    const onDoneRef = useRef(onDone)
    onDoneRef.current = onDone

    /** Rend `false` quand un envoi de ce service est déjà en vol : rien n'est parti. */
    const post = useCallback(
        (localId: string, threadId: string, body: unknown): boolean => {
            if (inFlightRef.current != null) return false
            inFlightRef.current = { localId, threadId }
            setBusy(true)
            dispatch(apiRequest(service, { threadId }, body) as never)
            gesture.submitted()
            return true
        },
        [dispatch, service, gesture]
    )

    useEffect(() => {
        const inFlight = inFlightRef.current
        if (inFlight == null || (!req?.success && !req?.error)) return
        if (String(req.routeParams?.threadId) !== inFlight.threadId) return
        inFlightRef.current = null
        setBusy(false)
        if (req.success) gesture.succeeded()
        else gesture.failed(req.error)
        onDoneRef.current(
            inFlight.localId,
            req.success ? { ok: true, server: req.all_data ?? null } : { ok: false, error: req.error ?? null }
        )
        dispatch(apiIdle(service) as never)
    }, [dispatch, service, req, gesture])

    useEffect(
        () => () => {
            dispatch(apiIdle(service) as never)
        },
        [dispatch, service]
    )

    return { post, busy }
}
